import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { FlashCell, fmtPrice, fmtPct, fmtMoney } from "../components/Format";
import { RefreshCw, TrendingUp, TrendingDown, Sparkles, AlertCircle, Bot as BotIcon } from "lucide-react";
import { toast } from "sonner";
import ProUpgradeCard from "../components/ProUpgradeCard";

export default function Signals() {
  const navigate = useNavigate();
  const [signals, setSignals] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [needsUpgrade, setNeedsUpgrade] = useState(false);

  const load = useCallback(async (opts = {}) => {
    if (opts.fresh) setLoading(true);
    try {
      const path = opts.fresh ? "/signals/refresh" : "/signals";
      const method = opts.fresh ? "post" : "get";
      const r = await api[method](path, opts.fresh ? null : undefined);
      setSignals(r.data.signals || []);
      setGeneratedAt(r.data.generated_at);
      setError(null);
      setNeedsUpgrade(false);
    } catch (e) {
      if (e.response?.status === 402) {
        setNeedsUpgrade(true);
        setError(null);
      } else {
        setError(e.response?.data?.detail || "Failed to load signals");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => load(), 60000); // auto-refresh every minute
    return () => clearInterval(id);
  }, [load]);

  async function refresh() {
    toast.message("Regenerating signals…");
    await load({ fresh: true });
    toast.success("Signals refreshed");
  }

  return (
    <div className="space-y-6" data-testid="signals-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-xs flex items-center gap-2">
            <Sparkles size={11} className="text-[#FFB300]" />
            AI Alpha · Claude Sonnet 4.5
          </div>
          <h1 className="text-3xl font-[Chivo] tracking-tight">Alpha Signals</h1>
          <p className="text-sm text-neutral-400 mt-1 max-w-2xl">
            Anonymized aggregate edges computed from every paper &amp; live bot on the platform — re-ranked every minute, distilled by AI.
          </p>
        </div>
        <Button
          data-testid="refresh-signals"
          onClick={refresh}
          disabled={loading}
          variant="outline"
          className="border-border bg-transparent hover:bg-[#141414]"
        >
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Generating…" : "Regenerate"}
        </Button>
      </div>

      <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-3 flex items-start gap-3" data-testid="not-advice-banner">
        <AlertCircle size={16} className="text-[#FF3B30] shrink-0 mt-0.5" />
        <div className="text-xs text-neutral-200">
          <span className="font-[Chivo] text-[#FF3B30] tracking-wide">NOT INVESTMENT ADVICE.</span>{" "}
          Signals are probabilistic observations from anonymized platform data, not predictions. See the{" "}
          <a href="/legal/disclaimer" target="_blank" rel="noreferrer" className="text-[#007AFF] hover:underline">Disclaimer</a>{" "}and{" "}
          <a href="/legal/risk" target="_blank" rel="noreferrer" className="text-[#007AFF] hover:underline">Risk Disclosure</a>{" "}before trading.
        </div>
      </div>

      {needsUpgrade ? (
        <ProUpgradeCard />
      ) : (
        <>
          {generatedAt && (
            <div className="text-xs text-neutral-500 num">
              Generated {new Date(generatedAt * 1000).toLocaleTimeString()} · {signals.length} edges ranked
            </div>
          )}

          {error && (
            <div className="border border-[#FF3B30] bg-[#FF3B30]/5 p-4 flex items-start gap-3" data-testid="signals-error">
              <AlertCircle size={16} className="text-[#FF3B30]" />
              <span className="text-sm text-[#FF3B30]">{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-px bg-border" data-testid="signals-grid">
            {signals.length === 0 && !loading && !error && (
              <div className="col-span-full bg-[#0F0F0F] p-10 text-center text-neutral-400">
                <Sparkles className="mx-auto mb-3 text-neutral-600" />
                <div className="font-[Chivo]">No signals yet</div>
                <div className="text-xs mt-1">Click Regenerate to compute fresh edges.</div>
              </div>
            )}
            {signals.map((s, i) => (
              <SignalCard key={`${s.symbol}-${s.side}`} signal={s} rank={i + 1} onSpawn={() => spawnBot(s)} />
            ))}
          </div>

          <div className="border border-border bg-[#0F0F0F] p-4 text-xs text-neutral-400 flex gap-3" data-testid="disclosure">
            <Sparkles size={14} className="text-[#FFB300] shrink-0 mt-0.5" />
            <span>
              Signals are derived from anonymized aggregate trade statistics; no individual user data is exposed. Past performance does not
              guarantee future results. Not financial advice — use as one input among many.
            </span>
          </div>
        </>
      )}
    </div>
  );

  function spawnBot(signal) {
    const cfg = {
      name: `${signal.symbol} ${signal.side === "long" ? "Long" : "Short"} · Signal`,
      strategy: (signal.strategies && signal.strategies[0]) || "scalping",
      asset_class: signal.asset_class === "crypto" ? "crypto" : "stock",
      symbol: signal.symbol,
      mode: "paper",
      exchange: signal.asset_class === "crypto" ? "coinbase" : "public",
    };
    sessionStorage.setItem("mt_spawn_bot", JSON.stringify(cfg));
    toast.message(`Spawning bot for ${signal.symbol}…`);
    navigate("/bots?spawn=1");
  }
}

function SignalCard({ signal, rank, onSpawn }) {
  const isLong = signal.side === "long";
  const color = isLong ? "#00E676" : "#FF3B30";
  const Icon = isLong ? TrendingUp : TrendingDown;
  const confColor = signal.confidence >= 75 ? "#00E676" : signal.confidence >= 60 ? "#FFB300" : "#A3A3A3";

  return (
    <div className="bg-[#0F0F0F] p-5 flex flex-col gap-3" data-testid={`signal-card-${signal.symbol}-${signal.side}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="label-xs num text-neutral-500">#{rank.toString().padStart(2, "0")}</span>
          <span className={`w-1.5 h-1.5 rounded-full ${signal.asset_class === "crypto" ? "bg-[#FFB300]" : "bg-[#007AFF]"}`} />
          <span className="font-[Chivo] text-lg">{signal.symbol}</span>
          <span className="label-xs">{signal.asset_class}</span>
        </div>
        <div className="flex items-center gap-1" style={{ color }}>
          <Icon size={14} />
          <span className="font-[Chivo] tracking-wide text-sm uppercase">{signal.side}</span>
        </div>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <div className="label-xs">Confidence</div>
          <div className="num text-2xl font-[Chivo]" style={{ color: confColor }}>{signal.confidence}</div>
        </div>
        <div className="text-right">
          <div className="num text-base"><FlashCell value={signal.price} format={(v) => `$${fmtPrice(v)}`} /></div>
          <div className={`num text-xs ${(signal.change_pct_24h ?? 0) >= 0 ? "profit" : "loss"}`}>{fmtPct(signal.change_pct_24h ?? 0)}</div>
        </div>
      </div>

      <div className="w-full h-1 bg-[#1a1a1a]">
        <div className="h-full" style={{ width: `${signal.confidence}%`, background: confColor }} />
      </div>

      {signal.rationale ? (
        <div className="text-sm text-neutral-200 leading-relaxed border-l-2 pl-3" style={{ borderColor: color }}>
          {signal.rationale}
        </div>
      ) : (
        <div className="text-xs text-neutral-500 italic">Rationale unavailable — see metrics below.</div>
      )}

      <div className="grid grid-cols-3 gap-2 text-xs border-t border-border pt-3">
        <Mini k="Win Rate" v={<span className="num">{signal.win_rate}%</span>} />
        <Mini k="Sample" v={<span className="num">{signal.trades}</span>} />
        <Mini k="Net PnL" v={
          <span className={`num ${(signal.net_pnl ?? 0) >= 0 ? "profit" : "loss"}`}>
            {(signal.net_pnl ?? 0) >= 0 ? "+" : ""}${fmtMoney(signal.net_pnl ?? 0)}
          </span>
        } />
      </div>

      {signal.strategies?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {signal.strategies.map((s) => (
            <span key={s} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-border text-neutral-400">
              {s.replace("_", " ")}
            </span>
          ))}
        </div>
      )}

      <Button
        data-testid={`spawn-bot-${signal.symbol}-${signal.side}`}
        onClick={onSpawn}
        variant="outline"
        className="mt-1 border-[#007AFF] text-[#007AFF] hover:bg-[#007AFF]/10 bg-transparent w-full"
      >
        <BotIcon size={14} className="mr-2" /> Spawn Bot From Signal
      </Button>
    </div>
  );
}

function Mini({ k, v }) {
  return (
    <div>
      <div className="label-xs">{k}</div>
      <div className="text-sm">{v}</div>
    </div>
  );
}
